/**
 * Reading and writing which model a workspace runs on.
 *
 * Master-only by RLS. The app itself never calls this to decide anything: the
 * server resolves the model from the same tables with the service role, so a
 * browser cannot influence which model its own calls use. This module exists
 * for the admin screen alone.
 *
 * @module services/aiModelService
 */

import { getSupabaseClient } from "@/config/supabase";
import { nullableArg } from "./dbBoundary";
import { logError } from "@/services/logger";

export interface CatalogEntry {
  provider: string;
  model: string;
  label: string;
  /** False for providers with no adapter yet — listed, not selectable. */
  enabled: boolean;
}

export interface ModelSelection {
  /** null is the global default every workspace inherits. */
  workspaceId: string | null;
  provider: string;
  model: string;
  updatedAt: string;
  /** null = never tested. Not the same as "broken". */
  verifiedOk: boolean | null;
  verifiedAt: string | null;
}

export const fetchCatalog = async (): Promise<CatalogEntry[]> => {
  const { data, error } = await getSupabaseClient()
    .from("ai_model_catalog")
    .select("provider, model, label, enabled")
    .order("provider")
    .order("model");
  if (error) {
    logError("AiModel", "catalog read failed", error);
    return [];
  }
  return (data ?? []) as CatalogEntry[];
};

export const fetchSelections = async (): Promise<ModelSelection[]> => {
  const { data, error } = await getSupabaseClient()
    .from("ai_models")
    .select("workspace_id, provider, model, updated_at, verified_ok, verified_at");
  if (error) {
    logError("AiModel", "selection read failed", error);
    return [];
  }
  return ((data ?? []) as Array<Record<string, unknown>>).map((r) => ({
    workspaceId: (r.workspace_id as string | null) ?? null,
    provider: String(r.provider),
    model: String(r.model),
    updatedAt: String(r.updated_at),
    verifiedOk: (r.verified_ok as boolean | null) ?? null,
    verifiedAt: (r.verified_at as string | null) ?? null,
  }));
};

/**
 * Set the model for one workspace, or for every workspace when id is null.
 *
 * Upsert rather than insert: the unique indexes make a second row for the same
 * target impossible, so without this a master's second save would fail with a
 * constraint error instead of changing the value.
 */
export const setModel = async (
  workspaceId: string | null,
  provider: string,
  model: string,
  /** Result of the test call, when one was run. null = not tested. */
  verifiedOk: boolean | null = null,
): Promise<{ ok: boolean; error?: string }> => {
  // Through the RPC, not a PostgREST upsert: the global row's uniqueness is a
  // partial index over (true), which ON CONFLICT cannot name from a client, so
  // a second save would fail on the constraint rather than replace the value.
  // The function also re-checks master and the catalogue's enabled flag.
  const { error } = await getSupabaseClient().rpc("set_ai_model", {
    p_workspace: nullableArg(workspaceId),
    p_provider: provider,
    p_model: model,
    p_verified_ok: nullableArg(verifiedOk),
  });
  if (error) {
    logError("AiModel", "save failed", error);
    return { ok: false, error: error.message };
  }
  return { ok: true };
};

/** Remove a workspace's override so it inherits the default again. */
export const clearModel = async (workspaceId: string): Promise<{ ok: boolean; error?: string }> => {
  const { error } = await getSupabaseClient()
    .from("ai_models")
    .delete()
    .eq("workspace_id", workspaceId);
  if (error) {
    logError("AiModel", "clear failed", error);
    return { ok: false, error: error.message };
  }
  return { ok: true };
};

/** Workspaces a master can assign a model to. */
export interface WorkspaceRow {
  id: string;
  name: string;
}

export const fetchWorkspaceRows = async (): Promise<WorkspaceRow[]> => {
  const { data, error } = await getSupabaseClient()
    .from("workspaces")
    .select("id, name")
    .order("name");
  if (error) {
    logError("AiModel", "workspace list failed", error);
    return [];
  }
  return ((data ?? []) as Array<Record<string, unknown>>).map((r) => ({
    id: String(r.id),
    name: String(r.name ?? ""),
  }));
};

/**
 * Ask the provider whether this model id actually answers.
 *
 * A free-text field makes a typo indistinguishable from a valid id until the
 * first real call fails, hours later, on someone else's screen. This turns that
 * into an answer before saving. Master-only, unmetered, one token.
 */
export const testModel = async (
  provider: string,
  model: string,
): Promise<{ ok: boolean; message: string }> => {
  try {
    const { data: session } = await getSupabaseClient().auth.getSession();
    const token = session.session?.access_token;
    if (!token) return { ok: false, message: "ยังไม่ได้เข้าสู่ระบบ" };

    const resp = await fetch("/api/ai-model-test", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ provider, model }),
    });
    const data = (await resp.json().catch(() => null)) as
      | { ok?: boolean; message?: string; error?: string }
      | null;
    if (!resp.ok) return { ok: false, message: data?.error ?? "ทดสอบไม่สำเร็จ" };
    return { ok: data?.ok === true, message: data?.message ?? "" };
  } catch (e) {
    logError("AiModel", "test failed", e);
    return { ok: false, message: e instanceof Error ? e.message : "ทดสอบไม่สำเร็จ" };
  }
};
